import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";
import { setRefCardVisible, isLoginCardSuppressed, subscribeLoginSuppressed } from "@/lib/refCard";
import { Gift, X, ArrowRight, Crown } from "lucide-react";

const DAY = 24 * 60 * 60 * 1000;
const track = (event, props) => api.post("/analytics/event", { events: [{ event, props: props || {} }] }).catch(() => {});

export default function ReferralLoginCard() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [show, setShow] = useState(false);
  const [suppressed, setSuppressed] = useState(isLoginCardSuppressed());

  useEffect(() => subscribeLoginSuppressed(setSuppressed), []);

  useEffect(() => {
    // Once per login (tab) session, and not within 24h of a dismissal.
    if (sessionStorage.getItem("pm_ref_card_seen")) return;
    const until = parseInt(localStorage.getItem("pm_ref_card_dismiss_until") || "0", 10);
    if (Date.now() < until) return;
    let stop = false;
    (async () => {
      try {
        const { data } = await api.get("/referral/me");
        if (stop || !data.enabled) return;
        const earned = data.rewards_earned > 0;
        const claimable = earned && data.recharge?.enabled && (data.recharge?.eligible || 0) > 0;
        if (earned && !claimable) return;   // already rewarded & nothing to claim -> nothing to show
        setD({ ...data, earned, claimable });
        setShow(true);
        setRefCardVisible(true);
        sessionStorage.setItem("pm_ref_card_seen", "1");
        track("referral_notification_shown", { x: data.valid_count, earned });
      } catch { /* silent */ }
    })();
    return () => { stop = true; setRefCardVisible(false); };
  }, []);

  const close = (e) => {
    e?.stopPropagation();
    setShow(false);
    setRefCardVisible(false);
    localStorage.setItem("pm_ref_card_dismiss_until", String(Date.now() + DAY));
    track("referral_notification_closed");
  };

  const openRefer = () => { track("referral_notification_opened"); nav("/app/refer"); };

  const shareNow = (e) => {
    e?.stopPropagation();
    track("referral_notification_share");
    const msg = (d.share_or || "") + (lang !== "or" && d.share_en ? "\n\n———\n\n" + d.share_en : "");
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (!show || !d || suppressed) return null;

  const target = d.target || 100;
  const x = d.valid_count || 0;
  const remaining = Math.max(0, target - x);
  const pct = Math.min(100, Math.round((x / target) * 100));

  // Earned + claimable variant
  if (d.earned && d.claimable) {
    return (
      <div onClick={openRefer} data-testid="referral-login-card"
        className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-thali-green to-thali-dark text-white shadow-warm cursor-pointer motion-safe:animate-fade-in-up">
        <div className="absolute inset-0 shimmer pointer-events-none" />
        <div className="relative flex items-center gap-3 p-3 min-h-[80px]">
          <span className="text-2xl shrink-0 motion-safe:pulse-once">👑</span>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-extrabold text-thali-turmeric text-sm leading-tight">
              {lang === "or" ? "ଆପଣ 1 ବର୍ଷ ମାଗଣା + ରିଚାର୍ଜ ପାଇଛନ୍ତି!" : "You earned 1 year FREE + recharge!"}
            </div>
            <div className="text-white/85 text-xs mt-0.5">{lang === "or" ? "ଆପଣଙ୍କ ରିଚାର୍ଜ ଦାବି କରନ୍ତୁ →" : "Claim your recharge →"}</div>
          </div>
          <button onClick={openRefer} className="shrink-0 bg-thali-saffron text-white font-bold rounded-full px-3 py-2 text-xs flex items-center gap-1" data-testid="ref-card-claim">
            {lang === "or" ? "ଦାବି କରନ୍ତୁ" : "Claim"} <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button onClick={close} className="shrink-0 w-8 h-8 grid place-items-center text-white/70" aria-label="Close" data-testid="ref-card-close"><X className="w-4 h-4" /></button>
        </div>
      </div>
    );
  }

  // Mega Reward progress variant
  return (
    <div onClick={openRefer} data-testid="referral-login-card"
      className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-thali-green to-thali-dark text-white shadow-warm cursor-pointer motion-safe:animate-fade-in-up">
      <div className="absolute inset-0 shimmer pointer-events-none" />
      <div className="relative flex items-center gap-3 p-3 pb-4 min-h-[80px]">
        <span className="text-2xl shrink-0 motion-safe:pulse-once"><Gift className="w-7 h-7 text-thali-turmeric" /></span>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-extrabold text-thali-turmeric text-[13px] md:text-sm leading-tight">
            {lang === "or"
              ? "100 ଜଣ ଶିକ୍ଷକଙ୍କୁ ରେଫର କରନ୍ତୁ → 1 ବର୍ଷ ମାଗଣା + 3 ମାସ ଅସୀମିତ ରିଚାର୍ଜ! 📱"
              : "Refer 100 teachers → 1 YEAR FREE + 3 MONTHS UNLIMITED RECHARGE! 📱"}
          </div>
          <div className="text-white/85 text-[11px] mt-0.5 truncate" data-testid="ref-card-progress-text">
            {x === 0
              ? (lang === "or" ? "ଆଜି ହିଁ ଆରମ୍ଭ କରନ୍ତୁ, ଗୋଟିଏ WhatsApp ଗ୍ରୁପରେ ସେୟାର କରନ୍ତୁ!" : "Start today, share in one WhatsApp group!")
              : (lang === "or"
                ? `ଆପଣଙ୍କ ନିଜ ନମ୍ବରରେ · ଆପଣଙ୍କର ${x}/${target} · ଆଉ ମାତ୍ର ${remaining} ବାକି!`
                : `On your own mobile number · You have ${x}/${target} · Only ${remaining} to go!`)}
          </div>
        </div>
        <button onClick={shareNow} className="shrink-0 bg-thali-saffron text-white font-bold rounded-full px-3 py-2 text-xs flex items-center gap-1" data-testid="ref-card-share">
          {lang === "or" ? "ଏବେ ସେୟାର କରନ୍ତୁ" : "Share now"} <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button onClick={close} className="shrink-0 w-8 h-8 grid place-items-center text-white/70" aria-label="Close" data-testid="ref-card-close"><X className="w-4 h-4" /></button>
      </div>
      {/* progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/20">
        <div className="h-full bg-thali-saffron transition-all" style={{ width: `${pct}%` }} data-testid="ref-card-progress-bar" />
      </div>
      <a href="/referral-terms" onClick={(e) => e.stopPropagation()} className="absolute right-2 bottom-2 text-[9px] text-white/60 underline">T&C apply</a>
    </div>
  );
}
